import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { useCurrencySymbol } from "../../../../utils/currencyUtils";
import CommaConvert from "../../../../utils/CommaConvert";
import { fetchDescriptionTasks } from "../../../../store/slices/DescriptionTask";
import { fetchAllQuotations } from "../../../../store/slices/generateQuotationSlice";
import { fetchHardwareDescriptions } from "../../../../store/slices/hardwareDescriptionSlice";
import { fetchOtherExpenses } from "../../../../store/slices/otherexpenses";
import { fetchSalesTaxes } from "../../../../store/slices/salesTaxesSlice";
import { fetchSalesHourlyRates } from "../../../../store/slices/salesHourlyRateSlice";
import { replaceProjectFinanceNotes } from "../../../../store/slices/projectFinanceNotesSlice";
import { getMyTrainings } from "../../../../store/slices/MyTraining/myTrainings";
import {
    isTrainingExpenseDisplayRow,
    isFinancePlSyntheticTrainingInjectionRow,
    isTrainingExpenseEntry,
    resolveInstallationProjectRowForTrainingRollup,
} from "../../../../utils/trainingOtherExpenseSync";
import {
    getPerdiemAndAverageCostFromExpenses,
    resolveEntityId,
    scopeOtherExpensesForUser,
} from "../../../../utils/salespersonRatesScope";

/** Training rows must not inflate manager P&L summary (effort + manual other expenses only). */
function isManagerPlTrainingCostRow(entry) {
    if (!entry || typeof entry !== "object") return false;
    if (isFinancePlSyntheticTrainingInjectionRow(entry)) return true;
    if (isTrainingExpenseEntry(entry)) return true;
    if (isTrainingExpenseDisplayRow(entry)) return true;
    const raw = String(entry?.description ?? entry?.Description ?? "").toLowerCase();
    if (raw.includes("training")) return true;
    return false;
}

/** Walk oldprojectid chain so reassigned managers include prior tracking row effort. */
export function collectManagerReassignmentProjectIds(
    trackingRows,
    allProjects = []
) {
    const ids = new Set();
    const projectById = new Map(
        (allProjects || []).map((project) => [String(project?._id), project])
    );
    const rows = Array.isArray(trackingRows)
        ? trackingRows
        : trackingRows
          ? [trackingRows]
          : [];

    rows.forEach((row) => {
        if (!row?._id) return;
        let cursor = String(row._id).trim();
        const seen = new Set();
        while (cursor && !seen.has(cursor)) {
            seen.add(cursor);
            ids.add(cursor);
            const prev = projectById.get(cursor);
            cursor = String(
                prev?.oldprojectid || prev?.oldProjectId || ""
            ).trim();
        }
    });

    return ids;
}

export function resolveProjectRatesSource(projectRow, allProjects = []) {
    if (!projectRow) return null;
    if (projectRow?.ManagerAssignedRates?.length) return projectRow;

    const quotationId = String(projectRow.quotationId ?? "").trim();
    const typeService = String(projectRow.TypeService ?? "").trim();
    const rowManagerId = String(
        projectRow.managerid || projectRow.managerId || ""
    ).trim();

    const candidates = (allProjects || []).filter(
        (p) =>
            p.isProjecttracking === false &&
            String(p.quotationId ?? "").trim() === quotationId &&
            String(p.TypeService ?? "").trim() === typeService
    );

    let quotedTwin = null;
    if (rowManagerId) {
        quotedTwin = candidates.find(
            (p) =>
                String(p.managerid || p.managerId || "").trim() === rowManagerId
        );
    }
    if (!quotedTwin) {
        quotedTwin = candidates.find((p) => p?.ManagerAssignedRates?.length);
    }
    if (!quotedTwin) {
        quotedTwin = candidates[0];
    }

    if (!quotedTwin) return projectRow;

    return {
        ...projectRow,
        ManagerAssignedRates: quotedTwin.ManagerAssignedRates || [],
        perdiem: quotedTwin.perdiem ?? projectRow.perdiem,
        avgcost:
            quotedTwin.avgcost ?? projectRow.avgcost ?? quotedTwin.averagecost,
    };
}

function findProjectRowForTask(task, allProjects, financeDataProject) {
    if (!task?.projectid) return null;
    const id = String(task.projectid);
    return (
        (financeDataProject || []).find((p) => String(p._id) === id) ||
        (allProjects || []).find((p) => String(p._id) === id)
    );
}

function findManagerAssignedRate(source, task) {
    const rates = source?.ManagerAssignedRates || [];
    const personId = String(task.taskownerid || "");
    if (!personId) return null;

    return (
        rates.find(
            (r) =>
                String(r.personid) === personId &&
                task.designation &&
                String(r.designation || "").trim() ===
                    String(task.designation || "").trim()
        ) || rates.find((r) => String(r.personid) === personId)
    );
}

/** L4 actual expenses: apply project.ManagerAssignedRates hourlyrate (non-hardware tasks). */
export function applyManagerAssignedRatesToProject(
    projectExpenseRow,
    allProjects = [],
    financeDataProject = []
) {
    if (!projectExpenseRow) return projectExpenseRow;

    const enrichList = (list) =>
        (list || []).map((item) => {
            const typeService = String(
                item.typeservice || item.TypeService || ""
            ).toLowerCase();
            if (typeService.includes("hardware")) {
                return {
                    ...item,
                    hourlysalaray: Number(item.hourlysalaray) || 0,
                    overheadpercentage: Number(item.overheadpercentage) || 0,
                };
            }

            const projectRow = findProjectRowForTask(
                item,
                allProjects,
                financeDataProject
            );
            const source = resolveProjectRatesSource(projectRow, allProjects);
            const matchedRate = findManagerAssignedRate(source, item);

            if (matchedRate) {
                return {
                    ...item,
                    hourlysalaray: Number(matchedRate.hourlyrate) || 0,
                    overheadpercentage: 0,
                };
            }

            return {
                ...item,
                hourlysalaray: Number(item.hourlysalaray) || 0,
                overheadpercentage: Number(item.overheadpercentage) || 0,
            };
        });

    return {
        ...projectExpenseRow,
        TaskList: enrichList(projectExpenseRow.TaskList),
        Description: [...(projectExpenseRow.Description || [])],
        HardwareList: [...(projectExpenseRow.HardwareList || [])],
    };
}

/** Combined hourly rate for manager actual P&L (reassigned manager uses current tracking row rates). */
export function getManagerActualHourlyRateForTask(
    task,
    trackingProjects,
    allProjects,
    managerQuotations
) {
    const taskProjectId = String(task.projectid || "");
    const currentManagerRows = trackingProjects || [];
    let rateProjectRow =
        currentManagerRows.find((p) => String(p._id) === taskProjectId) || null;

    if (!rateProjectRow && currentManagerRows.length && taskProjectId) {
        const chainIds = collectManagerReassignmentProjectIds(
            currentManagerRows,
            allProjects
        );
        if (chainIds.has(taskProjectId)) {
            rateProjectRow = currentManagerRows[0];
        }
    }

    if (!rateProjectRow) {
        rateProjectRow =
            (allProjects || []).find(
                (p) =>
                    String(p._id) === taskProjectId &&
                    p.isProjecttracking === true
            ) || null;
    }

    if (!rateProjectRow) {
        return (
            (Number(task.hourlysalaray) || 0) +
            (Number(task.overheadpercentage) || 0)
        );
    }

    const personId = String(task.taskownerid || "");
    const ratesSource = resolveProjectRatesSource(rateProjectRow, allProjects);
    const assignedRates = ratesSource?.ManagerAssignedRates || [];
    const fromAssigned =
        assignedRates.find(
            (r) =>
                String(r.personid) === personId &&
                task.designation &&
                String(r.designation || "").trim() ===
                    String(task.designation || "").trim()
        ) || assignedRates.find((r) => String(r.personid) === personId);

    if (fromAssigned && Number(fromAssigned.hourlyrate) > 0) {
        return Number(fromAssigned.hourlyrate);
    }

    const managerId = String(
        rateProjectRow?.managerid || rateProjectRow?.managerId || ""
    ).trim();
    if (managerId) {
        let matchedQuote = (managerQuotations || []).find(
            (q) =>
                String(q.personid || "") === personId &&
                resolveEntityId(q.createdBy) === managerId
        );
        if (!matchedQuote && task.designation) {
            matchedQuote = (managerQuotations || []).find(
                (q) =>
                    q.designation === task.designation &&
                    String(q.personid || "") === personId &&
                    resolveEntityId(q.createdBy) === managerId
            );
        }
        if (matchedQuote) {
            return (
                (Number(matchedQuote.hourlysalaray) || 0) +
                (Number(matchedQuote.overheadpercentage) || 0)
            );
        }
    }

    const role = String(task.designation || "").toLowerCase();
    if (role.includes("manager") && Number(rateProjectRow?.managerHourlyRate) > 0) {
        return Number(rateProjectRow.managerHourlyRate);
    }
    if (
        role.includes("operation") &&
        Number(rateProjectRow?.operationHourlyRate) > 0
    ) {
        return Number(rateProjectRow.operationHourlyRate);
    }

    const salary = Number(task.hourlysalaray) || 0;
    const overhead = Number(task.overheadpercentage) || 0;
    if (salary > 0 && overhead > 0) {
        return salary + overhead;
    }
    return salary || overhead;
}

const normalizeSalesEmail = (email) =>
    String(email || "").trim().toLowerCase();

function findManagerProjectRowById(projectId, projectsList, financeDataProjectList) {
    const id = String(projectId || "").trim();
    if (!id) return null;
    return (
        (financeDataProjectList || []).find((p) => String(p?._id) === id) ||
        (projectsList || []).find((p) => String(p?._id) === id) ||
        null
    );
}

function isInstallationServiceType(typeService) {
    return String(typeService || "").toLowerCase().includes("installation");
}

/** Per-diem / average cost for the salesperson who assigned the lead (Settings → Other Expenses). */
export function resolveInstallationExpenseRatesForProject(
    projectId,
    projectsList,
    otherExpensesList,
    { employees = [], financeDataProjectList = [], salespersonEmail = null } = {}
) {
    const matchedProjectsForId = [
        ...(financeDataProjectList || []),
        ...(projectsList || []),
    ].filter(
        (p, index, arr) =>
            String(p?._id || "") === String(projectId || "") &&
            arr.findIndex((x) => String(x?._id) === String(p?._id)) === index
    );

    const installationRow =
        resolveInstallationProjectRowForTrainingRollup(
            [{ projectid: projectId }],
            matchedProjectsForId
        ) || matchedProjectsForId[0];

    if (!installationRow) {
        return { perdiem: 0, averagecost: 0 };
    }

    const source = resolveProjectRatesSource(installationRow, projectsList);
    const frozenPerdiem = Number(source?.perdiem) || 0;
    const frozenAvg =
        Number(source?.avgcost ?? source?.averagecost) || 0;
    if (frozenPerdiem > 0 || frozenAvg > 0) {
        return { perdiem: frozenPerdiem, averagecost: frozenAvg };
    }

    const salesEmail =
        salespersonEmail ||
        installationRow?.SalesPersonEmail ||
        installationRow?.salespersonEmail ||
        null;

    let ownerUserId = "";
    if (salesEmail && employees?.length) {
        const salesperson = employees.find(
            (emp) =>
                normalizeSalesEmail(emp.EmpEmail) ===
                normalizeSalesEmail(salesEmail)
        );
        ownerUserId = resolveEntityId(salesperson?._id);
    }

    if (!ownerUserId) {
        ownerUserId = resolveEntityId(installationRow?.createdBy);
    }

    if (!ownerUserId) {
        return { perdiem: 0, averagecost: 0 };
    }

    const expensesForUser = scopeOtherExpensesForUser(otherExpensesList || [], {
        loggedUserId: ownerUserId,
    });
    return getPerdiemAndAverageCostFromExpenses(expensesForUser);
}

function appendManagerInstallationTripCosts(
    matchedProjectfinal,
    descriptionValue,
    {
        projects = [],
        financeDataProject = [],
        otherExpenses = [],
        employees = [],
        quotationSalespersonEmail = null,
    } = {}
) {
    const installationTripByProjectOwner = {};

    (matchedProjectfinal.Description || []).forEach((desc) => {
        if (isManagerPlTrainingCostRow(desc)) return;

        const projectRow = findManagerProjectRowById(
            desc.projectid,
            projects,
            financeDataProject
        );
        const typeService =
            desc.typeservice ||
            projectRow?.TypeService ||
            matchedProjectfinal?.TypeService ||
            "";
        if (!isInstallationServiceType(typeService) || !desc.tripduration) {
            return;
        }

        const ownerKey = String(desc.taskownerid || desc.projectid || "");
        const key = `${desc.projectid || ""}:${ownerKey}`;
        installationTripByProjectOwner[key] = Math.max(
            installationTripByProjectOwner[key] || 0,
            Number(desc.tripduration) || 0
        );
    });

    let tripTotal = descriptionValue;
    Object.entries(installationTripByProjectOwner).forEach(
        ([key, tripDuration]) => {
            const [projectId] = key.split(":");
            const scopedRates = resolveInstallationExpenseRatesForProject(
                projectId,
                projects,
                otherExpenses,
                {
                    employees,
                    financeDataProjectList: financeDataProject,
                    salespersonEmail:
                        quotationSalespersonEmail ||
                        matchedProjectfinal?.salespersonEmail ||
                        matchedProjectfinal?.SalesPersonEmail ||
                        null,
                }
            );
            tripTotal +=
                tripDuration * (Number(scopedRates?.perdiem) || 0) +
                tripDuration * (Number(scopedRates?.averagecost) || 0);
        }
    );

    return tripTotal;
}

/** Actual P&L summary: Σ hours spent × combined rate (per person) + non-training desc.expenses. */
export function computeManagerActualProjectExpense(
    matchedProjectfinal,
    trackingProjects = [],
    allProjects = [],
    managerQuotations = [],
    {
        otherExpenses = [],
        employees = [],
        financeDataProjectList = [],
        quotationSalespersonEmail = null,
        includeInstallationTripCosts = false,
    } = {}
) {
    if (!matchedProjectfinal) return 0;

    const hoursByOwner = {};
    (matchedProjectfinal.TaskList || []).forEach((task) => {
        if (isManagerPlTrainingCostRow(task)) return;
        const ownerId = String(task.taskownerid || "").trim();
        if (!ownerId) return;
        const hoursSpent = Number(task.hoursspent) || 0;
        if (!hoursSpent) return;
        if (!hoursByOwner[ownerId]) {
            hoursByOwner[ownerId] = { totalHours: 0, sampleTask: task };
        }
        hoursByOwner[ownerId].totalHours += hoursSpent;
    });

    const totalTaskValue = Object.values(hoursByOwner).reduce(
        (total, { totalHours, sampleTask }) => {
            const hourlyRate = getManagerActualHourlyRateForTask(
                sampleTask,
                trackingProjects,
                allProjects,
                managerQuotations
            );
            return total + totalHours * hourlyRate;
        },
        0
    );

    let descriptionValue = 0;
    (matchedProjectfinal.Description || []).forEach((desc) => {
        if (isManagerPlTrainingCostRow(desc)) return;
        const expenseAmt = Number(desc?.expenses);
        if (Number.isFinite(expenseAmt) && expenseAmt > 0) {
            descriptionValue += expenseAmt;
        }
    });

    if (includeInstallationTripCosts) {
        descriptionValue = appendManagerInstallationTripCosts(
            matchedProjectfinal,
            descriptionValue,
            {
                projects: allProjects,
                financeDataProject: financeDataProjectList,
                otherExpenses,
                employees,
                quotationSalespersonEmail,
            }
        );
    }

    let hardwareValue = 0;
    if (matchedProjectfinal.HardwareList?.length > 0) {
        const hardwareByDescription = {};
        matchedProjectfinal.HardwareList.forEach((hw) => {
            const description = hw.assemblepartdescription;
            if (!hardwareByDescription[description]) {
                hardwareByDescription[description] = hw;
            } else if (
                hw.totalprice > hardwareByDescription[description].totalprice
            ) {
                hardwareByDescription[description] = hw;
            }
        });
        Object.values(hardwareByDescription).forEach((hw) => {
            if (hw.totalprice) {
                hardwareValue += Number(hw.totalprice) || 0;
            }
        });
    }

    const grandTotal = totalTaskValue + descriptionValue + hardwareValue;
    return Number.isFinite(grandTotal) ? grandTotal : 0;
}

/**
 * Installation-manager P&L row total: hours × (hourly + overhead) + other-expense line items.
 * Quoted expenses: (rate + overhead) × hours assigned + desc.value + optional trip × salesperson perdiem/avgcost.
 * Actual expenses: hoursspent × rate + desc.expenses/value; optional trip × perdiem/avgcost.
 */
export function computeManagerQuotedProjectExpense(
    matchedProjectfinal,
    {
        preferActualHours = false,
        projects = [],
        financeDataProject = [],
        otherExpenses = [],
        includeInstallationTripCosts = false,
    } = {}
) {
    if (!matchedProjectfinal) return 0;

    const totalTaskValue = (matchedProjectfinal.TaskList || []).reduce((total, task) => {
        if (preferActualHours && isManagerPlTrainingCostRow(task)) {
            return total;
        }
        const hours = preferActualHours
            ? Number(task.hoursspent) || 0
            : Number(task.hoursassigned) ||
              Number(task.hoursspent) ||
              0;
        const hourlySalary = Number(task.hourlysalaray) || 0;
        const overhead = Number(task.overheadpercentage) || 0;
        return total + hours * (hourlySalary + overhead);
    }, 0);

    let descriptionValue = 0;

    (matchedProjectfinal.Description || []).forEach((desc) => {
        if (preferActualHours && isManagerPlTrainingCostRow(desc)) return;
        // Quoted: Amount column (desc.value). Actual: Expenses column (desc.expenses) only.
        const otherExpense = preferActualHours
            ? Number(desc?.expenses) || 0
            : Number(desc?.value) || 0;
        descriptionValue += otherExpense;
    });

    // Trip per-diem / average cost scoped to project.createdBy (assigning salesperson).
    if (includeInstallationTripCosts) {
        const installationTripByProjectOwner = {};

        (matchedProjectfinal.Description || []).forEach((desc) => {
            if (
                desc.typeservice &&
                desc.typeservice.toLowerCase().includes("installation") &&
                desc.taskownerid &&
                desc.tripduration
            ) {
                const key = `${desc.projectid || ""}:${desc.taskownerid}`;
                installationTripByProjectOwner[key] = Math.max(
                    installationTripByProjectOwner[key] || 0,
                    Number(desc.tripduration) || 0
                );
            }
        });

        Object.entries(installationTripByProjectOwner).forEach(
            ([key, tripDuration]) => {
                const [projectId] = key.split(":");
                const scopedRates = (otherExpenses || []).length
                    ? resolveInstallationExpenseRatesForProject(
                          projectId,
                          projects,
                          otherExpenses
                      )
                    : null;
                const projectRow =
                    (financeDataProject || []).find(
                        (p) => String(p._id) === String(projectId)
                    ) ||
                    (projects || []).find(
                        (p) => String(p._id) === String(projectId)
                    );
                const source = resolveProjectRatesSource(projectRow, projects);
                const installPerdiem =
                    Number(scopedRates?.perdiem) ||
                    Number(source?.perdiem) ||
                    0;
                const installAvgcost =
                    Number(scopedRates?.averagecost) ||
                    Number(source?.avgcost ?? source?.averagecost) ||
                    0;
                descriptionValue +=
                    tripDuration * installPerdiem +
                    tripDuration * installAvgcost;
            }
        );
    }

    let hardwareValue = 0;
    if (matchedProjectfinal.HardwareList?.length > 0) {
        const hardwareByDescription = {};
        matchedProjectfinal.HardwareList.forEach((hw) => {
            const description = hw.assemblepartdescription;
            if (!hardwareByDescription[description]) {
                hardwareByDescription[description] = hw;
            } else if (
                hw.totalprice > hardwareByDescription[description].totalprice
            ) {
                hardwareByDescription[description] = hw;
            }
        });
        Object.values(hardwareByDescription).forEach((hw) => {
            if (hw.totalprice) {
                hardwareValue += Number(hw.totalprice) || 0;
            }
        });
    }

    const grandTotal = totalTaskValue + descriptionValue + hardwareValue;
    return Number.isFinite(grandTotal) ? grandTotal : 0;
}

const ManagerActualAllServiceTable = ({
    handlerefresh,
    filteredQuotation,
    // showAssociatedCostTable,
    selectedProject,
    projects,
    calculatedProjects,
    handleAccociatedTabClick,
    setinstallationPersons,
    setSoftwarePersons,
    setCloudPersons,
    setHardwarePersons,
    tasks,
    items
}) => {
     const dispatch = useDispatch();
        const currency = useCurrencySymbol();
        const USAComma = CommaConvert();
        const { enqueueSnackbar } = useSnackbar();
        // const { quotations } = useSelector((state) => state.hourlyRateQuotation);
        const { employees } = useSelector((state) => state.employees);
        const { items: otherExpenses } = useSelector((state) => state.otherExpenses);
        const { taxes } = useSelector((state) => state.salesTaxes);
        const myTrainingsRaw = useSelector((state) => state.myTrainings?.mytrainings);
        const { data } = useSelector(
            (state) => state.salesHourlyRate
        );
        const [GandATax, setGandATax] = useState(0);
        const [taxValue, setTaxValue] = useState(0);
        const [selected, setSelected] = useState(false);
        const [totalServiceGrandTotal, setTotalServiceGrandTotal] = useState(0);
        const tableContainerRef = useRef(null);
        const [totalGrossProfit, setTotalGrossProfit] = useState(0);
        const [totalGAExpenses, setTotalGAExpenses] = useState(0);
        const [totalTax, setTotalTax] = useState(0);
        const [totalNetRevenue, setTotalNetRevenue] = useState(0);
        const [netRevenuePercentage, setNetRevenuePercentage] = useState(0);
        const [totalHoursSum, setTotalHoursSum] = useState(0);
        const [saveTimeout, setSaveTimeout] = useState({});
        const [notes, setNotes] = useState({});
    
        useEffect(() => {
            dispatch(fetchDescriptionTasks());
            dispatch(fetchAllQuotations());
            dispatch(fetchHardwareDescriptions());
            dispatch(fetchOtherExpenses());
            dispatch(fetchSalesTaxes());
            dispatch(fetchSalesHourlyRates());
            dispatch(getMyTrainings());
    
        }, [dispatch]);

        const myTrainingsList = useMemo(() => {
            const payload = myTrainingsRaw;
            if (Array.isArray(payload?.trainings)) return payload.trainings;
            if (Array.isArray(payload)) return payload;
            return [];
        }, [myTrainingsRaw]);

        const tasksWithTrainingCost = useMemo(() => {
            const baseTasks = Array.isArray(tasks) ? tasks : [];
            const trainings = myTrainingsList;
            if (!filteredQuotation || !trainings?.length) return baseTasks;

            // Scope to currently opened project(s) only to avoid affecting other calculations.
            const scopedProjectIds = new Set();
            const projectIdToTypeServices = new Map();

            Object.values(filteredQuotation || {}).forEach((typeServices) => {
                Object.values(typeServices || {}).forEach((itemsArr) => {
                    (itemsArr || []).forEach((row) => {
                        const pid = String(row?.ProjectID || row?.projectId || "").trim();
                        if (!pid) return;
                        scopedProjectIds.add(pid);
                        const typeService = String(row?.TypeService || row?.typeService || "").trim();
                        if (typeService) {
                            const set = projectIdToTypeServices.get(pid) || new Set();
                            set.add(typeService);
                            projectIdToTypeServices.set(pid, set);
                        }
                    });
                });
            });

            if (!scopedProjectIds.size) return baseTasks;

            const existingTrainingKeys = new Set(
                baseTasks
                    .filter((t) => isTrainingExpenseDisplayRow(t))
                    .map((t) => {
                        const pid = String(t?.projectid || t?.projectId || "").trim();
                        const owner = String(t?.taskownerid || "").trim();
                        const amount = Number(t?.expenses ?? t?.value ?? 0) || 0;
                        return `${pid}::${owner}::${amount}`;
                    })
            );

            const synthetic = [];
            const seenSynthetic = new Set();

            trainings.forEach((training) => {
                const isTrainingQuoted =
                    training?.isTrainingQuoted === true ||
                    String(training?.isTrainingQuoted || "").toLowerCase() === "true";
                if (!isTrainingQuoted) return;

                const trainingCost = Number(training?.trainingcost ?? training?.trainingCost ?? 0) || 0;
                if (!trainingCost) return;

                const projectId = String(training?.projectid || training?.projectId || "").trim();
                if (!projectId || !scopedProjectIds.has(projectId)) return;

                // Note: backend uses employee _id here (same as taskownerid in tasks).
                const assignedTo = String(training?.assignedto || training?.assignedTo || "").trim();
                if (!assignedTo) return;

                // If the same training cost is already present as a task, don't inject.
                const dedupeKey = `${projectId}::${assignedTo}::${trainingCost}`;
                if (existingTrainingKeys.has(dedupeKey)) return;

                const typeServices = Array.from(projectIdToTypeServices.get(projectId) || []);
                const targets = typeServices.length ? typeServices : [""];

                targets.forEach((typeService, idx) => {
                    const syntheticKey = `${training?._id || dedupeKey}-${projectId}-${typeService || "all"}-${idx}`;
                    if (seenSynthetic.has(syntheticKey)) return;
                    seenSynthetic.add(syntheticKey);

                    synthetic.push({
                        _id: `training-cost-${syntheticKey}`,
                        projectid: projectId,
                        taskownerid: assignedTo,
                        description: "Training cost",
                        expenses: trainingCost,
                        value: trainingCost,
                        tripduration: 0,
                        ...(typeService ? { typeservice: typeService } : {}),
                        isSyntheticTrainingCost: true,
                    });
                });
            });

            if (!synthetic.length) return baseTasks;
            return [...baseTasks, ...synthetic];
        }, [tasks, myTrainingsList, filteredQuotation]);
    
        useEffect(() => {
            taxes?.forEach((data) => {
                if (data.taxName === "G&A Expenses") {
                    setGandATax(data.taxPercentage);
                } else if (data.taxName === "Tax") {
                    setTaxValue(data.taxPercentage);
                }
            });
        }, [taxes]);

        // Function to get description values for a specific project and person
        const getDescriptionValues = useCallback(
            (projectID, personID, typeService) => {
                if (!tasksWithTrainingCost) return [];
                return tasksWithTrainingCost.filter(
                    (desc) => desc.projectid === projectID && desc.taskownerid === personID
                );
            },
            [tasksWithTrainingCost]
        );

        const getMyTrainingAmountForAssignee = useMemo(() => {
            const trainingsList = myTrainingsList;

            const resolveRefId = (ref) => {
                if (ref == null || ref === "") return "";
                if (typeof ref === "object") return String(ref?._id ?? ref?.id ?? "").trim();
                return String(ref).trim();
            };

            return (project, assigneeId) => {
                if (!project || !assigneeId) return 0;
                const assignee = resolveRefId(assigneeId);
                if (!assignee) return 0;

                const projectId = String(project?._id || project?.ProjectID || "").trim().toLowerCase();
                const quotationId = String(project?.quotationId || project?.quotationID || project?.quotationid || "").trim();
                const projectName = String(project?.ProjectName || project?.projectname || "").trim().toLowerCase();
                const leadId = String(project?.leadid || project?.leadId || project?.LeadId || project?.LeadID || "").trim();

                const matched = trainingsList.filter((t) => {
                    const assignedTo = resolveRefId(t?.assignedto ?? t?.assignedTo);
                    if (!assignedTo || assignedTo !== assignee) return false;

                    const tProjectId = resolveRefId(t?.projectid ?? t?.projectId ?? t?.project).toLowerCase();
                    const tQuotationId = String(t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? "").trim();
                    const tProjectName = String(t?.projectname || "").trim().toLowerCase();
                    const tLeadId = String(t?.leadid ?? t?.leadId ?? "").trim();

                    return (
                        (projectId && tProjectId && projectId === tProjectId) ||
                        (quotationId && tQuotationId && quotationId === tQuotationId) ||
                        (projectName && tProjectName && projectName === tProjectName) ||
                        (leadId && tLeadId && leadId === tLeadId)
                    );
                });

                return matched.reduce((sum, t) => {
                    const amount = Number(
                        String(t?.trainingcost ?? t?.trainingCost ?? 0)
                            .toString()
                            .replace(/,/g, "")
                    );
                    return sum + (Number.isFinite(amount) ? amount : 0);
                }, 0);
            };
        }, [myTrainingsList]);
    
    
    
        const getTotalDescriptionValue = useCallback(
            (projectID, personID, typeService) => {
                const descriptions = getDescriptionValues(projectID, personID, typeService);
                    const installationTripByOwner = {};
                let descriptionValue = 0;
                descriptions.forEach((desc) => {
    
                    // For Installation services, track trip duration per owner
                    if (desc.typeservice && desc.typeservice.toLowerCase().includes('installation') &&
                        desc.taskownerid && desc.tripduration) {
                        if (!installationTripByOwner[desc.taskownerid]) {
                            installationTripByOwner[desc.taskownerid] = desc.tripduration;
                        } else {
                            // Use the maximum trip duration for each owner
                            installationTripByOwner[desc.taskownerid] =
                                Math.max(installationTripByOwner[desc.taskownerid], desc.tripduration);
                        }
                    }
    
                    // Add the normal value calculation
                    const otherExpense =
                        Number(desc?.expenses) ||
                        Number(desc?.value) ||
                        0;
                    descriptionValue += otherExpense;
                });
    
                // Add Installation trip duration calculations (per diem + average cost for project owner)
                if (
                    typeService &&
                    typeService.toLowerCase().includes("installation") &&
                    Object.keys(installationTripByOwner).length > 0
                ) {
                    const { perdiem: projectPerdiem, averagecost: projectAveragecost } =
                        resolveInstallationExpenseRatesForProject(
                            projectID,
                            projects,
                            otherExpenses
                        );
                    Object.values(installationTripByOwner).forEach((tripDuration) => {
                        descriptionValue +=
                            tripDuration * projectPerdiem + tripDuration * projectAveragecost;
                    });
                }

                if (String(typeService || "").toLowerCase().includes("hardware")) {
                    const projectCtx = {
                        _id: projectID,
                        ProjectID: projectID,
                        ProjectName: selectedProject?.ProjectName,
                        quotationId: selectedProject?.quotationId,
                        leadid: selectedProject?.leadId ?? selectedProject?.leadid,
                    };
                    descriptionValue += getMyTrainingAmountForAssignee(projectCtx, personID);
                }

                return descriptionValue;
            },
            [getDescriptionValues, otherExpenses, projects, getMyTrainingAmountForAssignee, selectedProject]
        );
    
        const getHardwareValues = useCallback(
            (projectID, typeService) => {
                if (!items) return [];
                return items.filter(
                    (desc) =>
                        desc.projectid === projectID &&
                        // desc.taskownerid === personID &&
                        desc.typeservice === typeService
                );
            },
            [items]
        );
    
        const getTotalHardwareCostValue = useCallback(
            (projectID, personID, typeService) => {
                // const hardwareCost = getHardwareValues(projectID, personID, typeService);
                let hardwareValue = 0;
                const hardwareCost = getHardwareValues(projectID, typeService);
                const hardwareByDescription = {};
    
                // Group hardware by assemblepartdescription and keep only the one with highest totalprice
                hardwareCost.forEach((hw) => {
                    const description = hw.assemblepartdescription;
    
                    if (!hardwareByDescription[description]) {
                        hardwareByDescription[description] = hw;
                    } else {
                        // If we already have this description, keep the one with higher totalprice
                        if (hw.totalprice > hardwareByDescription[description].totalprice) {
                            hardwareByDescription[description] = hw;
                        }
                    }
                });
    
                // Calculate total hardware value using only the highest totalprice items
                Object.values(hardwareByDescription).forEach((hw) => {
                    if (hw.totalprice) {
                        hardwareValue += hw.totalprice;
                    }
                });
                return hardwareValue
                // return hardwareCost.reduce((sum, desc) => sum + (desc.totalprice || 0), 0);
            },
            [getHardwareValues]
        );
    
        const [groupedByPersonState, setGroupedByPersonState] = useState([]);
        const [updatedGroupedByPerson, setUpdatedGroupedByPerson] = useState([]);
        const [finalData, setFinalData] = useState([]);
        const [allProjectsOnly, setAllProjectsOnly] = useState([]);
        const [matchedDescription, setMatchedDescription] = useState(0);
        const [matchedHardware, setMatchedHardware] = useState(0);
        const [totalCostSum, setTotalCostSum] = useState("");
        const resolveHourlyRateForPerson = useCallback((item, personId, roles) => {
            let matchedQuotation = null;
            if (item?.quotationDetails?.length) {
                matchedQuotation = item.quotationDetails.find(
                    (q) => String(q.personid || "") === String(personId || "")
                );
                if (!matchedQuotation && roles?.length) {
                    for (const role of roles) {
                        matchedQuotation = item.quotationDetails.find((q) => {
                            const designation = q.designation?.toLowerCase() || "";
                            const cleanRole = role?.toLowerCase() || "";
                            return (
                                designation.includes(cleanRole) ||
                                cleanRole.includes(designation)
                            );
                        });
                        if (matchedQuotation) break;
                    }
                }
            }
            return matchedQuotation
                ? (Number(matchedQuotation.hourlysalaray) || 0) +
                      (Number(matchedQuotation.overheadpercentage) || 0)
                : 0;
        }, []);

        // Function to generate calculation data
        const generateCalculationData = useCallback(() => {
            if (!filteredQuotation) return [];
    
            const data = [];
    
            Object.entries(filteredQuotation).forEach(([projectName, typeServices]) => {
                Object.entries(typeServices).forEach(([typeService, items]) => {
                    items.forEach((item) => {
                        if (!item?.WorkEntries || item.WorkEntries.length === 0) return;
    
                        const groupedByPerson = {};
                        const personRoleMap = {};
    
                        // Group work entries by person (quoted: use assigned/planned hours)
                        item.WorkEntries.forEach((entry) => {
                            const personName = entry?.createdBy?.name;
                            const personRoles = entry?.createdBy?.role || [];
                            const personId = entry?.taskownerid;
    
                            if (!groupedByPerson[personName]) {
                                groupedByPerson[personName] = {
                                    totalHours: 0,
                                    personId: personId,
                                };
                            }
                            groupedByPerson[personName].totalHours +=
                                Number(entry.hoursassigned) ||
                                Number(entry.hoursspent) ||
                                0;
    
                            if (!personRoleMap[personName]) {
                                personRoleMap[personName] = {
                                    roles: personRoles,
                                    personId: personId,
                                };
                            }
                        });
    
                        // Create data objects for each person
                        Object.entries(groupedByPerson).forEach(([name, personData]) => {
                            const personId = personData.personId;
                            const totalHours = personData.totalHours;
                            const roles = personRoleMap[name]?.roles || [];
    
                            // Find employee details
                            const currentEmployee = employees.find((emp) => {
                                const fullName = `${emp.EmpFirstName} ${emp.EmpLastName}`
                                    .trim()
                                    .toLowerCase();
                                return fullName === name.trim().toLowerCase();
                            });
    
                            const capitalizedRoles =
                                currentEmployee?.customRoles?.map(
                                    (role) =>
                                        role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
                                ) || [];
    
                            const hourlyRate = resolveHourlyRateForPerson(
                                item,
                                personId,
                                roles
                            );
                            const totalCost = totalHours * hourlyRate;
    
                            // Get description and hardware totals
                            const descriptionTotal = getTotalDescriptionValue(
                                item.ProjectID,
                                personId,
                                item.TypeService
                            );
    
                            const hardwareTotal = getTotalHardwareCostValue(
                                item.ProjectID,
                                personId,
                                item.TypeService
                            );
    
                            const personTotal = totalCost + descriptionTotal + hardwareTotal;
    
                            // Add to calculation data array
                            data.push({
                                projectName,
                                projectId: item.ProjectID,
                                typeService: item.TypeService,
                                personName: name,
                                personId,
                                designation: `${item.TypeService} ${capitalizedRoles[0] || " "}`,
                                hours: item?.TypeService?.toLowerCase().includes("hardware") ? 0 : totalHours,
                                // hours: item.TypeService === "Hardware" ? 0 : totalHours,
                                hourlyRate: item?.TypeService?.toLowerCase().includes("hardware") ? 0 : hourlyRate,
                                // hourlyRate: item.TypeService === "Hardware" ? 0 : hourlyRate,
                                effortCost: item?.TypeService?.toLowerCase().includes("hardware") ? 0 : totalCost,
                                // effortCost: item.TypeService === "Hardware" ? 0 : totalCost,
                                descriptionCost: item?.TypeService?.toLowerCase().includes("hardware") ? 0 : descriptionTotal,
                                // descriptionCost: item.TypeService === "Hardware" ? 0 : descriptionTotal,
                                hardwareCost: item?.TypeService?.toLowerCase().includes("hardware") ? hardwareTotal : 0,
                                // hardwareCost: item.TypeService === "Hardware" ? hardwareTotal : 0,
                                totalCost: personTotal
                            });
                        });
                    });
                });
            });
    
            return data;
        }, [
            filteredQuotation,
            employees,
            getTotalDescriptionValue,
            getTotalHardwareCostValue,
            resolveHourlyRateForPerson,
        ]);
    
        // Update calculation data whenever filteredQuotation changes
        useEffect(() => {
            const data = generateCalculationData();
    
            const totalEffortCost = data.reduce((sum, item) => sum + (item.effortCost || 0), 0);
            setTotalCostSum(totalEffortCost);
        }, [filteredQuotation, generateCalculationData]);
    
        // Rest of your useEffect hooks remain the same
        useEffect(() => {
            if (!filteredQuotation) return;
    
            const newGrouped = {};
    
            Object.entries(filteredQuotation).forEach(([projectName, typeServices]) => {
                Object.entries(typeServices).forEach(([typeService, items]) => {
                    items.forEach((item) => {
                        if (!item?.WorkEntries || item.WorkEntries.length === 0) return;
    
                        item.WorkEntries.forEach((entry) => {
                            const personName = entry?.createdBy?.name;
                            const personId = entry?.taskownerid;
                            const personRoles = entry?.createdBy?.role[0] || [];
                            const matchedEmp = employees.find((emp) => emp._id === personId);
    
                            let hourlyRate = 0;
                            if (item?.quotationDetails?.length > 0) {
                                const matchedDetail = item.quotationDetails.find((qd) => {
                                    const designation = qd.designation?.toLowerCase() || "";
                                    const typeServiceStr = item.TypeService?.toLowerCase() || "";
                                    const empService = matchedEmp?.serviceType?.toLowerCase() || "";
    
                                    if (!designation.includes(empService)) return false;
                                    if (typeServiceStr && !designation.includes(typeServiceStr))
                                        return false;
    
                                    return true;
                                });
    
                                if (matchedDetail) {
                                    hourlyRate = matchedDetail.hourlysalaray || 0;
                                }
                            }
    
                            if (!newGrouped[personName]) {
                                newGrouped[personName] = {
                                    name: personName,
                                    totalHours: 0,
                                    personId,
                                    roles: personRoles,
                                    projects: [],
                                };
                            }
    
                            newGrouped[personName].totalHours += entry.hoursspent || 0;
                            newGrouped[personName].projects.push({
                                projectId: item.ProjectID,
                                typeService: item.TypeService,
                                hourlyRate,
                            });
                        });
                    });
                });
            });
    
            const groupedArray = Object.values(newGrouped);
            setGroupedByPersonState(groupedArray);
        }, [filteredQuotation, employees]);
    
        // Other useEffect hooks remain unchanged
        useEffect(() => {
            if (groupedByPersonState?.length && employees?.length) {
                const updated = groupedByPersonState.map((person) => {
                    const matchedEmp = employees.find((emp) => emp._id === person.personId);
    
                    if (matchedEmp) {
                        return {
                            ...person,
                            designation:
                                matchedEmp.servicetype + " " + matchedEmp.customRoles[0],
                        };
                    }
                    return person;
                });
    
                setUpdatedGroupedByPerson(updated);
            }
        }, [groupedByPersonState, employees]);
    
        useEffect(() => {
            if (
                updatedGroupedByPerson?.length &&
                employees?.length &&
                data?.length
            ) {
                const updated = updatedGroupedByPerson.map((person) => {
                    const matchedEmp = employees.find((emp) => emp._id === person.personId);
                    const matchedQuotation = data.find(
                        (quo) =>
                            quo.designation?.toLowerCase() === person.designation?.toLowerCase()
                    );
    
                    return {
                        ...person,
                        TypeService: matchedEmp?.servicetype || null,
                        hourlysalaray: matchedQuotation?.hourlysalaray + matchedQuotation?.overheadpercentage || null,
                    };
                });
    
                setFinalData(updated);
            }
        }, [updatedGroupedByPerson, employees, data]);
    
        useEffect(() => {
            if (finalData?.length) {
                const projectsArray = finalData.flatMap(
                    (person) => person.projects || []
                );
                const projectIds = projectsArray.map((project) => project.projectId);
    
                setAllProjectsOnly(projectIds);
            }
        }, [finalData]);
    
    
        useEffect(() => {
            if (allProjectsOnly?.length && tasksWithTrainingCost?.length) {
                const matchedDescriptions = tasksWithTrainingCost.filter((desc) =>
                    allProjectsOnly.includes(desc.projectid)
    
                );
    
                let descriptionValue = 0;
                if (matchedDescriptions?.length > 0) {
                    const installationTripByProjectOwner = {};

                    matchedDescriptions.forEach((desc) => {
                        if (
                            desc.typeservice &&
                            desc.typeservice.toLowerCase().includes("installation") &&
                            desc.taskownerid &&
                            desc.tripduration
                        ) {
                            const tripKey = `${desc.projectid || ""}|${desc.taskownerid}`;
                            installationTripByProjectOwner[tripKey] = Math.max(
                                installationTripByProjectOwner[tripKey] || 0,
                                Number(desc.tripduration) || 0
                            );
                        }

                        const otherExpense =
                            Number(desc?.expenses) ||
                            Number(desc?.value) ||
                            0;
                        descriptionValue += otherExpense;
                    });

                    Object.entries(installationTripByProjectOwner).forEach(
                        ([tripKey, tripDuration]) => {
                            const [projectId] = tripKey.split("|");
                            const { perdiem: projectPerdiem, averagecost: projectAveragecost } =
                                resolveInstallationExpenseRatesForProject(
                                    projectId,
                                    projects,
                                    otherExpenses
                                );
                            descriptionValue +=
                                tripDuration * projectPerdiem +
                                tripDuration * projectAveragecost;
                        }
                    );
                }


                setMatchedDescription(descriptionValue);
            }
        }, [allProjectsOnly, tasksWithTrainingCost, projects, otherExpenses]);
    
        useEffect(() => {
            if (allProjectsOnly?.length && items?.length) {
                const matchedHardwareItems = items.filter((item) =>
                    allProjectsOnly.includes(item.projectid)
                );
    
                // Group hardware by assemblepartdescription and keep only the one with highest totalprice
                const hardwareByDescription = {};
                matchedHardwareItems.forEach((hw) => {
                    const description = hw.assemblepartdescription;
    
                    if (!hardwareByDescription[description]) {
                        hardwareByDescription[description] = hw;
                    } else {
                        // If we already have this description, keep the one with higher totalprice
                        if (hw.totalprice > hardwareByDescription[description].totalprice) {
                            hardwareByDescription[description] = hw;
                        }
                    }
                });
    
                // Calculate total hardware value using only the highest totalprice items
                const totalHardwareValue = Object.values(hardwareByDescription).reduce(
                    (sum, hw) => sum + (hw.totalprice || 0),
                    0
                );
    
                setMatchedHardware(totalHardwareValue);
            }
        }, [allProjectsOnly, items]);

        const calculateGrossProfit = (projectName, serviceGrandTotal, typeService, projectID) => {
            const quotedValuePerService = selectedProject.QuotedValue / Object.keys(groupedData).length;
            const grossProfit = quotedValuePerService - serviceGrandTotal;
            return grossProfit;
        };
    
        const handleClick = () => {
            setSelected(!selected);
        };
    
    
        const transformFilteredQuotation = useCallback((filteredQuotation, employees) => {
    
            const result = [];
    
            Object.entries(filteredQuotation || {}).forEach(([projectName, typeServices]) => {
                Object.entries(typeServices).forEach(([typeService, items]) => {
                    items.forEach((item) => {
                        if (!item?.WorkEntries?.length) return;
    
                        // Group work entries by person
                        const groupedByPerson = {};
                        const personRoleMap = {};
                        item.WorkEntries.forEach((entry) => {
                            const personName = entry?.createdBy?.name;
                            const personRoles = entry?.createdBy?.role || [];
                            const personId = entry?.taskownerid;
    
                            if (!groupedByPerson[personName]) {
                                groupedByPerson[personName] = { totalHours: 0, personId };
                            }
                            groupedByPerson[personName].totalHours += entry.hoursspent || 0;
    
                            if (!personRoleMap[personName]) {
                                personRoleMap[personName] = { roles: personRoles, personId };
                            }
                        });
    
                        // Convert grouped persons into array with costs
                        Object.entries(groupedByPerson).forEach(([name, personData]) => {
                            const totalHours = personData.totalHours;
                            const personId = personData.personId;
                            const roles = personRoleMap[name]?.roles || [];
                            const ProjectID = item?.ProjectID;
                            const ProjectNotes = item?.ProjectNotes
                            const ServiceQuotedValue = item?.QuotedValue
    
    
                            let finalMatchedQuotation = item?.quotationDetails?.find(
                                (q) => String(q.personid || "") === String(personId || "")
                            );
    
                            if (!finalMatchedQuotation && item?.quotationDetails?.length) {
                                for (const role of roles) {
                                    finalMatchedQuotation = item.quotationDetails.find(
                                        (q) => {
                                            const designation =
                                                q.designation?.toLowerCase() || "";
                                            const cleanRole = role?.toLowerCase() || "";
                                            return (
                                                designation.includes(cleanRole) ||
                                                cleanRole.includes(designation)
                                            );
                                        }
                                    );
                                    if (finalMatchedQuotation) break;
                                }
                            }
    
                            // // Match quotation details
                            // const matchedQuotation = item?.quotationDetails?.find((q) =>
                            //     roles.some((role) =>
                            //         q.designation?.toLowerCase().includes(role?.toLowerCase())
                            //     )
                            // );
    
                            const currentEmployee = employees.find((emp) => {
                                const fullName = `${emp.EmpFirstName} ${emp.EmpLastName}`.trim().toLowerCase();
                                return fullName === name.trim().toLowerCase();
                            });
    
                            const capitalizedRoles =
                                currentEmployee?.customRoles?.map(
                                    (role) =>
                                        role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
                                ) || [];
    
                            const hourlyRate =
                                (finalMatchedQuotation?.hourlysalaray || 0) +
                                (finalMatchedQuotation?.overheadpercentage || 0);
    
                            const totalCost = totalHours * hourlyRate;
    
                            // External helpers (you already have these)
                            const descriptionTotal = getTotalDescriptionValue(
                                item.ProjectID,
                                personId,
                                item.TypeService
                            );
    
                            const hardwareTotal = getTotalHardwareCostValue(
                                item.ProjectID,
                                personId,
                                item.TypeService
                            );
                            const hasProjectManagerRole = capitalizedRoles.includes("Project manager");
                            result.push({
                                ServiceQuotedValue,
                                projectName,
                                typeService: item.TypeService,
                                resourceType: item.ResourceType,
                                personId,
                                ProjectID,
                                ProjectNotes,
                                personName: name,
                                roles: capitalizedRoles,
                                totalHours,
                                hourlyRate,
                                totalCost,
                                descriptionTotal,
                                hardwareTotal,
                                // grandTotal:
                                //     item?.TypeService?.toLowerCase().includes("hardware")
                                //         // item.TypeService === "Hardware"
                                //         ? hardwareTotal + descriptionTotal
                                //         : descriptionTotal + totalCost,
                                grandTotal:
                                    item?.TypeService?.toLowerCase().includes("hardware")
                                        ? (hasProjectManagerRole ? hardwareTotal : 0) + descriptionTotal
                                        : descriptionTotal + totalCost,
                            });
                        });
                    });
                });
            });
    
            return result;
        }, [getTotalDescriptionValue, getTotalHardwareCostValue]);
        const groupByTypeService = (processedData) => {
            return processedData.reduce((acc, curr) => {
                if (!acc[curr.typeService]) {
                    acc[curr.typeService] = [];
                }
                acc[curr.typeService].push(curr);
                return acc;
            }, {});
        };
    
    
        const processedData = useMemo(() => {
            return transformFilteredQuotation(filteredQuotation, employees);
        }, [filteredQuotation, employees, transformFilteredQuotation]);
    
        const groupedData = useMemo(() => {
            return groupByTypeService(processedData);
        }, [processedData]);

    
        useEffect(() => {
            if (groupedData && Object.keys(groupedData).length > 0) {
    
                let sum = 0;
                Object.entries(groupedData).forEach(([typeService, rows]) => {
                    const isHardware = typeService?.toLowerCase().includes("hardware");
                    const hasManager = rows.some(row => {
                        const role = row.roles[0]?.toLowerCase();
                        return role?.includes("manager") || role?.includes("project manager") || role?.includes("pm");
                    });
    
                    const serviceGrandTotal = rows.reduce((acc, row, idx) => {
                        let rowGrandTotal = Number(row.grandTotal) || 0;
    
                        // If hardware, no manager, and this is the first employee, include hardwareTotal
                        if (isHardware && !hasManager && idx === 0) {
                            rowGrandTotal = (Number(row.hardwareTotal) || 0) + (Number(row.descriptionTotal) || 0);
                        }
    
                        return acc + rowGrandTotal;
                    }, 0);
                    sum += serviceGrandTotal;
                });
                setTotalServiceGrandTotal(sum);
            }
        }, [groupedData]);
    
        useEffect(() => {
            if (selected && tableContainerRef.current) {
                // Scroll to the right end of the container
                const scrollToPosition = tableContainerRef.current.scrollWidth;
                tableContainerRef.current.scrollTo({
                    left: scrollToPosition,
                    behavior: 'smooth'
                });
            }
        }, [selected]);
    
    
    
        useEffect(() => {
            if (groupedData && Object.keys(groupedData).length > 0) {
                let serviceSum = 0;
                let grossProfitSum = 0;
                let gaExpensesSum = 0;
                // let ebitSum = 0;
                let taxSum = 0;
                let netRevenueSum = 0;
    
                Object.entries(groupedData).forEach(([typeService, rows]) => {
                    const isHardware = typeService?.toLowerCase().includes("hardware");
                    const hasManager = rows.some(row => {
                        const role = row.roles[0]?.toLowerCase();
                        return role?.includes("manager") || role?.includes("project manager") || role?.includes("pm");
                    });
    
                    const serviceGrandTotal = rows.reduce((acc, row, idx) => {
                        let rowGrandTotal = Number(row.grandTotal) || 0;
    
                        // If hardware, no manager, and this is the first employee, include hardwareTotal
                        if (isHardware && !hasManager && idx === 0) {
                            rowGrandTotal = (Number(row.hardwareTotal) || 0) + (Number(row.descriptionTotal) || 0);
                        }
    
                        return acc + rowGrandTotal;
                    }, 0);
                    serviceSum += serviceGrandTotal;
    
                    const quotedValuePerService = selectedProject.QuotedValue / Object.keys(groupedData).length;
                    const grossProfit = quotedValuePerService - serviceGrandTotal;
                    const gaExpenses = grossProfit * (GandATax / 100);
                    const ebit = grossProfit - gaExpenses;
                    const tax = grossProfit * (taxValue / 100);
                    const netRevenue = ebit - tax;
    
                    grossProfitSum += grossProfit;
                    gaExpensesSum += gaExpenses;
                    // ebitSum += ebit;
                    taxSum += tax;
                    netRevenueSum += netRevenue;
                });
    
                // Calculate net revenue percentage
                const netRevenuePct = selectedProject.QuotedValue > 0
                    ? (netRevenueSum / selectedProject.QuotedValue) * 100
                    : 0;
    
                setTotalServiceGrandTotal(serviceSum);
                setTotalGrossProfit(grossProfitSum);
                setTotalGAExpenses(gaExpensesSum);
                // setTotalEBIT(ebitSum);
                setTotalTax(taxSum);
                setTotalNetRevenue(netRevenueSum);
                setNetRevenuePercentage(netRevenuePct);
            }
        }, [groupedData, selectedProject.QuotedValue, GandATax, taxValue]);
    
        useEffect(() => {
            if (groupedData && Object.keys(groupedData).length > 0) {
                let hoursSum = 0;
    
                Object.values(groupedData).forEach(rows => {
                    rows.forEach(row => {
                        // Only add hours for non-Hardware services
                        if (row.typeService !== "Hardware") {
                            hoursSum += row.totalHours || 0;
                        }
                    });
                });
    
                setTotalHoursSum(hoursSum);
            }
        }, [groupedData]);
    
        const saveNoteToAPI = useCallback(async (typeService, projectID, noteText) => {
            try {
                dispatch(replaceProjectFinanceNotes({ id: projectID, ProjectFinanceNotes: noteText }));
            } catch (error) {
                console.error('Error saving note:', error);
            } finally {
                enqueueSnackbar("Notes Added Successfully", { variant: "success" });
    
            }
        }, [dispatch, enqueueSnackbar]);
    
        const handleNoteChange = useCallback((typeService, projectID, noteText) => {
    
            // Create a unique key for this project-service combination
            const noteKey = `${projectID}-${typeService}`;
    
            // Update local state immediately for responsive UI
            setNotes(prev => ({
                ...prev,
                [noteKey]: noteText
            }));
    
            // Clear existing timeout for this noteKey
            if (saveTimeout[noteKey]) {
                clearTimeout(saveTimeout[noteKey]);
            }
    
            // Set new timeout for API call (debounce of 1 second)
            const newTimeout = setTimeout(() => {
                saveNoteToAPI(typeService, projectID, noteText);
            }, 2000);
    
            // Update timeout reference
            setSaveTimeout(prev => ({
                ...prev,
                [noteKey]: newTimeout
            }));
        }, [saveNoteToAPI, saveTimeout]);
    
        useEffect(() => {
            // Extract and set data for each service type with fallback to empty array
            setSoftwarePersons(groupedData.Software || []);
            setCloudPersons(groupedData.Cloud || []);
            setHardwarePersons(groupedData.Hardware || []);
            setinstallationPersons(groupedData.Installation || []);
    
        }, [groupedData, setSoftwarePersons, setCloudPersons, setHardwarePersons, setinstallationPersons]);
  return (
      <div className="pt-10 bg-white rounded-md">

          <div className="text-lg flex gap-2 font-semibold mb-2 pl-2">
              <h2 className="capitalize">Project Cost: {selectedProject.ProjectName}</h2>
              <button
                  onClick={handleClick}
                  className="flex items-center space-x-2 focus:outline-none"
              >
                  {/* Circle */}
                  <span
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected ? "border-blue-600" : "border-gray-400"
                          }`}
                  >
                      {selected && <span className="w-3 h-3 bg-blue-600 rounded-full"></span>}
                  </span>

                  {/* Label */}
                  <span
                      className={`text-base font-medium ${selected ? " font-semibold" : "text-gray-600"
                          }`}
                  >
                      Itemized P&amp;L
                  </span>
              </button>
          </div>

          <div
              ref={tableContainerRef}
              style={{ scrollBehavior: 'smooth' }}
              className="maintable w-full overflow-auto">
              <div className="w-full flex text-xs font-semibold">
                  <div className="flex-shrink-0 first-line:flex flex justify-start  align-middle items-center p-4 border text-centestartr w-52 bg-[#E8E8E8] ">
                      Resources Designation
                  </div>
                  <div className="flex flex-shrink-0 justify-center  align-middle items-center p-4 border text-center w-40 bg-[#E8E8E8] ">
                      Hours
                  </div>
                  <div className="flex flex-shrink-0 justify-center  align-middle items-center p-4 border text-center  w-40 bg-[#E8E8E8] ">
                      Hourly Rate ({currency}/hr)
                  </div>
                  <div className="flex flex-col flex-shrink-0 justify-center align-middle items-center p-4 border text-center w-40 bg-[#E8E8E8]">
                      Total Efforts ({currency})
                      <br />
                      <p className="text-[12px]">(Hours * Hourly Rate)</p>
                  </div>
                  <div className="flex flex-shrink-0 justify-center  align-middle items-center p-4 border text-center w-40 bg-[#E8E8E8]">
                      Associated Cost ({currency})
                  </div>
                  <div className="flex flex-col flex-shrink-0 justify-center  align-middle items-center p-3 border text-center w-40 bg-[#E8E8E8] ">
                      Total Cost ({currency})
                      <br />
                      <p className="text-[12px]">(Total Efforts + Associated Cost)</p>
                  </div>
                  <div className="flex flex-shrink-0 justify-center  align-middle items-center p-4 border text-center w-40 bg-[#E8E8E8] ">
                      Total Cost Per Service ({currency})
                  </div>
                  {selected && (
                      <div className="flex border">
                          <div className="flex-shrink-0 w-40 p-4 gap-2 flex justify-center align-middle items-center text-center border tracking-wider capitalize bg-[#E8E8E8]">
                              Quoted Value ({currency})
                          </div>

                          <div className="flex-shrink-0 w-40 p-4 flex justify-center align-middle items-center text-center border tracking-wider capitalize bg-[#E8E8E8]">
                              Gross Profit ({currency})
                          </div>
                          <div className="flex-shrink-0 w-40 p-4 flex justify-center align-middle items-center text-center border tracking-wider capitalize bg-[#E8E8E8]">
                              G&A Expenses ({currency})
                          </div>
                          {/* <div className="flex-shrink-0 w-24 p-4flex justify-center align-middle items-center text-center border border-black tracking-wider capitalize bg-[#1973eb] text-white">
                        EBIT ({currency})
                      </div> */}
                          <div className="flex-shrink-0 w-24 p-4 flex justify-center align-middle items-center text-center border tracking-wider capitalize bg-[#E8E8E8]">
                              Tax ({currency})
                          </div>
                          <div className="flex-shrink-0 w-52 flex flex-col justify-center align-middle items-center text-center border tracking-wider capitalize bg-[#E8E8E8]">
                              <div className="h-[50%] w-full flex justify-center align-middle items-center"> Net Revenue</div>
                              <div className="h-[50%] flex w-full">
                                  <div className="w-[50%] border flex justify-center align-middle items-center">({currency})</div>
                                  <div className="w-[50%] border flex justify-center align-middle items-center">(%)</div>
                              </div>

                          </div>
                          <div className="flex-shrink-0 w-24 p-4 flex justify-center align-middle items-center text-center border text-xs tracking-wider capitalize bg-[#E8E8E8]">
                              Notes
                          </div>
                      </div>
                  )}
              </div>

              <div className="Employee_Efforts_Calculation w-auto">
                  {Object.entries(groupedData)
                      // move Hardware last
                      .sort(([a], [b]) => {
                          if (a?.toLowerCase().includes("hardware")) return 1;
                          if (b?.toLowerCase().includes("hardware")) return -1;
                          return 0;
                      })
                      .map(([typeService, rows], serviceIndex) => {
                          // 👉 calculate subtotal for this service
                          const isHardware = typeService?.toLowerCase().includes("hardware");
                          const hasManager = rows.some(row => {
                              const role = row.roles[0]?.toLowerCase();
                              return role?.includes("manager") || role?.includes("project manager") || role?.includes("pm");
                          });
                          const serviceGrandTotal = rows.reduce(
                              (acc, row, idx) => {
                                  let rowGrandTotal = Number(row.grandTotal) || 0;

                                  // If hardware, no manager, and this is the first employee, include hardwareTotal
                                  if (isHardware && !hasManager && idx === 0) {
                                      rowGrandTotal = (Number(row.hardwareTotal) || 0) + (Number(row.descriptionTotal) || 0);
                                  }

                                  return acc + rowGrandTotal;
                              },
                              0
                          );

                          // Determine background color based on even/odd index
                          // const backgroundColor = serviceIndex % 2 === 0 ? "bg-white" : "bg-[#d9e0ef]";

                          return (
                              <div key={typeService} className="flex flex-col">
                                  <div className={`flex text-xs `}>
                                      <div className="border-b">
                                          {(() => {
                                              // Group rows by manager
                                              const groupedByManager = {};
                                              let currentManager = null;
                                              let managerCounter = 0;

                                              // First pass: group employees under their managers
                                              rows.forEach((row) => {
                                                  const role = row.roles[0]?.toLowerCase();

                                                  if (role?.includes("manager") || role?.includes("project manager") || role?.includes("pm")) {
                                                      // This is a manager - start a new group
                                                      managerCounter++;
                                                      currentManager = `manager${managerCounter}`;
                                                      groupedByManager[currentManager] = {
                                                          manager: row,
                                                          employees: []
                                                      };
                                                  } else if (currentManager) {
                                                      // This is an employee - add to current manager's group
                                                      groupedByManager[currentManager].employees.push(row);
                                                  } else {
                                                      // No manager found yet, create a default group
                                                      if (!groupedByManager.default) {
                                                          groupedByManager.default = {
                                                              manager: null,
                                                              employees: []
                                                          };
                                                      }
                                                      groupedByManager.default.employees.push(row);
                                                  }
                                              });

                                              // Second pass: render all groups
                                              const renderedRows = [];
                                              Object.entries(groupedByManager).forEach(([managerKey, group]) => {
                                                  let employeeCounter = 0;
                                                  let operationsCounter = 0;

                                                  // Render manager first (if exists)
                                                  if (group.manager) {
                                                      const managerNumber = managerKey.replace('manager', '');
                                                      renderedRows.push(
                                                          <div key={`${managerKey}-manager`} className={`flex `}>
                                                              <div className="w-52 py-1 px-4 capitalize flex-shrink-0 flex justify-start items-center text-start">
                                                                  {`${typeService} Manager ${managerNumber}`}
                                                              </div>
                                                              <div className="w-40 py-1 px-4  flex-shrink-0 flex justify-center items-center text-center">
                                                                  {typeService?.toLowerCase().includes("hardware") ? "" : group.manager.totalHours}
                                                              </div>
                                                              <div className="w-40 py-1 px-4  flex-shrink-0 flex justify-center items-center text-center">
                                                                  {typeService?.toLowerCase().includes("hardware") ? "" : USAComma(group.manager.hourlyRate)}
                                                              </div>
                                                              <div className="w-40 py-1 px-4  flex-shrink-0 flex justify-center items-center text-center">
                                                                  {typeService?.toLowerCase().includes("hardware")
                                                                      ? USAComma(group.manager.hardwareTotal)
                                                                      : USAComma(group.manager.totalCost)
                                                                  }
                                                              </div>
                                                              <div className="w-40 py-1 px-4  flex-shrink-0 flex justify-center items-center text-center">
                                                                  {USAComma(group.manager.descriptionTotal)}
                                                              </div>
                                                              <div className="w-40 py-1 px-4  flex-shrink-0 flex justify-center items-center text-center">
                                                                  {USAComma(group.manager.grandTotal)}
                                                              </div>
                                                          </div>
                                                      );
                                                  }

                                                  // Render employees under this manager
                                                  group.employees.forEach((employee, idx) => {
                                                      const role = employee.roles[0]?.toLowerCase();
                                                      let displayText = '';

                                                      if (role === "operations") {
                                                          operationsCounter++;
                                                          displayText = `${typeService} Employee ${operationsCounter}`;
                                                      } else {
                                                          employeeCounter++;
                                                          displayText = `${typeService} Employee ${employeeCounter}`;
                                                      }

                                                      // Check if hardware service, no manager, and this is the first employee
                                                      const isHardwareNoManager = typeService?.toLowerCase().includes("hardware") && !group.manager;
                                                      const isFirstEmployee = idx === 0;
                                                      const shouldShowHardwareValue = isHardwareNoManager && isFirstEmployee;

                                                      // Calculate grandTotal: if hardware, no manager, and first employee, include hardwareTotal
                                                      const calculatedGrandTotal = shouldShowHardwareValue
                                                          ? (employee.hardwareTotal || 0) + (employee.descriptionTotal || 0)
                                                          : employee.grandTotal;

                                                      renderedRows.push(
                                                          <div key={`${managerKey}-employee-${idx}`} className={`flex `}>
                                                              <div className="w-52 py-1 px-4  capitalize flex-shrink-0 flex justify-start items-center text-start ">
                                                                  {displayText}
                                                              </div>
                                                              <div className="w-40 py-1 px-4  flex-shrink-0 flex justify-center items-center text-center ">
                                                                  {typeService?.toLowerCase().includes("hardware") ? "" : employee.totalHours}
                                                              </div>
                                                              <div className="w-40 py-1 px-4  flex-shrink-0 flex justify-center items-center text-center ">
                                                                  {typeService?.toLowerCase().includes("hardware") ? "" : USAComma(employee.hourlyRate)}
                                                              </div>
                                                              <div className="w-40 py-1 px-4  flex-shrink-0 flex justify-center items-center text-center">
                                                                  {typeService?.toLowerCase().includes("hardware")
                                                                      ? (shouldShowHardwareValue ? USAComma(employee.hardwareTotal) : "")
                                                                      : USAComma(employee.totalCost)
                                                                  }
                                                              </div>
                                                              <div className="w-40 py-1 px-4  flex-shrink-0 flex justify-center items-center text-center">
                                                                  {USAComma(employee.descriptionTotal)}
                                                              </div>
                                                              <div className="w-40 py-1 px-4  flex-shrink-0 flex justify-center items-center text-center">
                                                                  {USAComma(calculatedGrandTotal)}
                                                              </div>
                                                          </div>
                                                      );
                                                  });
                                              });

                                              return renderedRows;
                                          })()}
                                      </div>

                                      {/* 👉 Service Grand Total column (visible on larger screens) */}
                                      <div className={`grandTotal w-40 flex-shrink-0 py-1 px-4 border-b hidden md:flex justify-center items-center text-center  `}>
                                          {USAComma(serviceGrandTotal)}
                                      </div>

                                      {selected && (
                                          <div className={`flex `}>

                                              <div className="w-40 flex-shrink-0 py-1 px-4  border-b text-wrap truncate break-all flex justify-center items-start text-center">
                                                  {USAComma(selectedProject.QuotedValue / Object.keys(groupedData).length)}
                                              </div>


                                              {/* Gross Profit */}
                                              <div className={`w-40 flex-shrink-0 py-1 px-4  border-b flex justify-center items-center text-center text-wrap truncate break-all
                  ${calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) < 0 ? "text-red-500" : "text-black"}`}>
                                                  {USAComma(calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID))}
                                              </div>

                                              {/* G&A Expenses */}
                                              <div className={`w-40 flex-shrink-0 py-1 px-4  border-b flex justify-center items-center text-center text-wrap truncate break-all
                  ${calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (GandATax / 100) < 0 ? "text-red-500" : "text-black"}`}>
                                                  {USAComma(calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (GandATax / 100))}
                                              </div>

                                              {/* Tax */}
                                              <div className={`w-24 flex-shrink-0 py-1 px-4 border-b  flex justify-center items-center text-center text-wrap truncate break-all 
                  ${calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (taxValue / 100) < 0 ? "text-red-500" : "text-black"} `}>
                                                  {USAComma(calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (taxValue / 100))}
                                              </div>

                                              {/* Net Revenue */}
                                              <div className="w-52 flex-shrink-0  flex justify-center items-center border-b text-center text-wrap truncate break-all">
                                                  <div className={`w-[50%] h-full  flex justify-center items-center text-start 
                    ${((calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) - (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (GandATax / 100))) - (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (taxValue / 100))) < 0 ? "text-red-500" : "text-black"} `}>
                                                      {USAComma(
                                                          (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) -
                                                              (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (GandATax / 100))) -
                                                          (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (taxValue / 100))
                                                      )}
                                                  </div>
                                                  <div className={`w-[50%] h-full  flex justify-center items-center text-center 
                    ${(
                                                          ((
                                                              (
                                                                  (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) - (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (GandATax / 100)))
                                                                  - (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (taxValue / 100))
                                                              ) /
                                                              rows[0]?.ServiceQuotedValue) * 100
                                                          ).toFixed(2)
                                                      ) < 0 ? "text-red-500" : (
                                                          ((
                                                              (
                                                                  (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) - (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (GandATax / 100)))
                                                                  - (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (taxValue / 100))
                                                              ) /
                                                              rows[0]?.ServiceQuotedValue) * 100
                                                          ).toFixed(2)
                                                      ) < 10 ? "text-orange-600" : "text-black"
                                                      } `}>
                                                      {(
                                                          ((
                                                              (
                                                                  (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) - (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (GandATax / 100)))
                                                                  - (calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID) * (taxValue / 100))
                                                              ) /
                                                              rows[0]?.ServiceQuotedValue) * 100

                                                              // (selectedProject.QuotedValue / Object.keys(groupedData).length)) * 100
                                                          ).toFixed(2)
                                                      )}
                                                  </div>
                                              </div>

                                              {/* Notes Input */}
                                              <div className="w-24 flex-shrink-0 py-1 px-4  border-b  flex justify-center items-start text-center text-wrap truncate break-all">
                                                  <textarea
                                                      className={`noteinput w-24 h-full outline-none text-center break-words whitespace-normal resize-none overflow-x-hidden`}
                                                      value={notes[`${rows[0]?.ProjectID}-${typeService}`] ?? rows[0]?.ProjectNotes ?? ''}
                                                      onChange={(e) => handleNoteChange(typeService, rows[0]?.ProjectID, e.target.value)}
                                                      placeholder="Enter Notes"
                                                  />
                                              </div>
                                          </div>
                                      )}
                                  </div>
                              </div>
                          );
                      })}
              </div>

              <div className="Totaltable flex w-full text-start text-sm font-semibold">
                  <div className="flex-shrink-0   py-1 px-4 border-b text-start  w-52 ">
                      Total
                  </div>

                  <div className="flex-shrink-0  py-1 px-4 border-b text-center w-40 ">
                      {totalHoursSum}
                  </div>
                  <div className="flex-shrink-0   py-1 px-4 border-b text-center w-40 ">
                      <div className="flex justify-around items-center"></div>
                  </div>

                  <div className="flex-shrink-0  py-1 px-4 border-b text-center w-40">
                      {USAComma(totalCostSum + matchedHardware)}
                  </div>
                  <div className="descriptionvaluetotal  py-1 px-4 border-b flex-shrink-0 p-1 text-center w-40">
                      {USAComma(matchedDescription)}
                  </div>
                  <div className="alltotal flex-shrink-0 py-1 px-4 border-b  text-center w-40 ">
                      {USAComma(totalServiceGrandTotal)}
                  </div>
                  <div className="sumOFserviceGrandTotal flex-shrink-0 py-1 px-4  border-b  text-center w-40">
                      {USAComma(totalServiceGrandTotal)}
                  </div>
                  {selected && (
                      <div className="flex">
                          <div className="alltotal flex-shrink-0 py-1 px-4 border-b  text-center w-40 ">
                              {/* {USAComma(
                                    Object.values(groupedData).reduce((total, rows) => {
                                        const serviceKey = `${rows[0]?.ProjectID}-${rows[0]?.typeService}`;
                                        const quotedValue = quotedValues[serviceKey] !== undefined
                                            ? parseFloat(quotedValues[serviceKey]) || 0
                                            : rows[0]?.ServiceQuotedValue || 0;
                                        return total + quotedValue;
                                    }, 0)
                                )} */}

                              {USAComma(selectedProject.QuotedValue)}

                          </div>
                          <div className={`sumofGrossProfit flex-shrink-0 py-1 px-4  border-b  text-center w-40  ${totalGrossProfit < 0 ? "text-red-500" : "text-black"
                              }`}>
                              {USAComma(totalGrossProfit)}
                          </div>
                          <div className={`sumofGAExpenses flex-shrink-0 py-1 px-4  border-b  text-center w-40  ${totalGAExpenses < 0 ? "text-red-500" : "text-black"
                              }`}>
                              {USAComma(totalGAExpenses)}
                          </div>
                          {/* <div className={`sumofEBIT flex-shrink-0 p-1 border border-black text-center w-24 bg-[#e7ebfb] ${totalEBIT < 0 ? "text-red-500" : "text-black"
                        }`}>
                        {USAComma(totalEBIT)}
                      </div> */}
                          <div className={`sumofTax flex-shrink-0 py-1 px-4  border-b text-center w-24  ${totalTax < 0 ? "text-red-500" : "text-black"
                              }`}>
                              {USAComma(totalTax)}
                          </div>
                          <div className="sumofNetRevenue flex flex-shrink-0  border-b text-center w-52 ">
                              <div className={`w-[50%] h-full   flex justify-center items-center text-center ${totalNetRevenue < 0 ? "text-red-500" : "text-black"
                                  }`}>
                                  {USAComma(totalNetRevenue)}
                              </div>
                              <div className={`w-[50%] h-full   flex justify-center items-center text-center ${netRevenuePercentage < 0 ? "text-red-500" :
                                  netRevenuePercentage < 10 ? "text-orange-600" : "text-black"
                                  }`}>
                                  {netRevenuePercentage.toFixed(2)}
                              </div>
                          </div>
                          <div className="alltotal flex-shrink-0 py-1 px-4  border-b text-center w-24 ">
                          </div>
                      </div>
                  )}
              </div>
          </div>


      </div>
  )
}

export default ManagerActualAllServiceTable